import { Flex, Box, Text } from "rebass/styled-components";
import useTranslation from "next-translate/useTranslation";

import { GetCaregiverQuery } from "../../generated/graphql";
import Icon from "../common/Icon";
import { subSeconds, formatDistanceToNow } from "date-fns";
import { useContext } from "react";
import { LocaleContext } from "../locale";
import { enGB, es, de, fr, da, nb, sv } from "date-fns/locale";

const locales = {
  en: enGB,
  "en-IE": enGB,
  es: es,
  fr: fr,
  de: de,
  da: da,
  no: nb,
  sv: sv
};

interface Props {
  caregiver: GetCaregiverQuery["caregiver"];
}

interface StatProps {
  icon: string;
  label: string;
  value: string;
}

const Stat = ({ icon, label, value }: StatProps) => {
  return (
    <Flex
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        my: 2,
        px: 2,
        fontSize: 2,
        lineHeight: "body",
        color: "veryDarkGray"
      }}
    >
      <Flex
        sx={{
          alignItems: "center"
        }}
      >
        <Icon name={icon}></Icon>
        <Text pl={1}>{label}</Text>
      </Flex>
      <Text
        sx={{
          fontWeight: "medium"
        }}
      >
        {value}
      </Text>
    </Flex>
  );
};

const Statistics = ({ caregiver }: Props) => {
  const { t } = useTranslation("common");
  const { locale } = useContext(LocaleContext);

  if (
    !caregiver?.lastSeenAt &&
    !caregiver?.responseTime &&
    !caregiver?.bookingsRatio &&
    !caregiver?.answerRatio
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        border: "default",
        borderRadius: "default",
        backgroundColor: "samoyedWhite"
      }}
    >
      {caregiver.lastSeenAt && (
        <Stat
          icon="clock"
          value={formatDistanceToNow(new Date(caregiver.lastSeenAt), {
            addSuffix: true,
            locale: locales[locale]
          })}
          label={t("lastSeenAt")}
        />
      )}
      {caregiver.responseTime && (
        <Stat
          icon="response"
          value={formatDistanceToNow(
            subSeconds(new Date(), parseInt(caregiver.responseTime)),
            {
              locale: locales[locale]
            }
          )}
          label={t("responseTime")}
        />
      )}
      {caregiver.bookingsRatio && (
        <Stat
          icon="booking-ratio"
          value={`${parseInt(caregiver.bookingsRatio)}%`}
          label={t("acceptedBookings")}
        />
      )}
      {caregiver.answerRatio && (
        <Stat
          icon="answer-ratio"
          value={`${parseInt(caregiver.answerRatio)}%`}
          label={t("responseRate")}
        />
      )}
    </Box>
  );
};

export default Statistics;
